import {
  Alert,
  Badge,
  Button,
  Card,
  CopyButton,
  Divider,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Text,
} from "@mantine/core";
import React from "react";
import { IconAlertCircle, IconCheck, IconCopy, IconRefresh, IconSparkles } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { usePostProjectByProjectIdIssuesAndIssueIdRunBreeamCheckMutation } from "../../../../../../../openapi";
import { useAppSelector } from "../../../../../../hooks";
import {
  selectBreeamIssueFilesQuery,
  selectBreeamModalHeader,
  selectBreeamPrerequisites,
  selectBreeamRunArgs,
  selectCopyableAiAssessmentText,
  selectEffectiveVerdict,
  selectParsedAiAssessment,
  selectTechnicalAnalysis,
  selectVerdictColor,
} from "./IssueBreeamCriterionCheckModal.selectors";

const getErrorMessage = (error: unknown) => {
  if (error == null) return "Ukjent feil";
  if (typeof error === "string") return error;
  if (typeof error === "object" && "data" in error) {
    const data = (error as { data?: unknown }).data;
    if (typeof data === "string") return data;
    return JSON.stringify(data);
  }
  return JSON.stringify(error);
};

const SectionCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Card withBorder radius="md" padding="md">
    <Stack gap="sm">
      <Text fw={600}>{title}</Text>
      {children}
    </Stack>
  </Card>
);

const IssueBreeamCriterionCheckModal: React.FC<{
  onClose: () => void;
}> = ({ onClose }) => {
  const hasStartedInitialRunRef = useRef(false);
  const [showTechnicalDebug, setShowTechnicalDebug] = useState(false);
  const [
    runBreeamCriterionCheck,
    { data: result = null, error: runError, isLoading: isRunning, reset: resetBreeamCriterionCheck },
  ] = usePostProjectByProjectIdIssuesAndIssueIdRunBreeamCheckMutation();
  useAppSelector(selectBreeamIssueFilesQuery);
  const breeamRunArgs = useAppSelector(selectBreeamRunArgs);
  const { inputAttributeLabel, issueId, issueTitle } = useAppSelector(selectBreeamModalHeader);

  const { canRunCheck, isCheckingPrerequisites, missingPrerequisites } = useAppSelector(selectBreeamPrerequisites);
  const canStartCheck = canRunCheck && breeamRunArgs != null;

  useEffect(() => {
    if (!canStartCheck || hasStartedInitialRunRef.current) {
      return;
    }

    hasStartedInitialRunRef.current = true;
    void runBreeamCriterionCheck(breeamRunArgs);
  }, [breeamRunArgs, canStartCheck, runBreeamCriterionCheck]);

  const rerunCheck = () => {
    if (!canStartCheck) {
      return;
    }

    resetBreeamCriterionCheck();
    void runBreeamCriterionCheck(breeamRunArgs);
  };

  const parsedAiAssessment = selectParsedAiAssessment(result);
  const effectiveVerdict = selectEffectiveVerdict({ parsedAiAssessment, result });
  const verdictColor = selectVerdictColor(effectiveVerdict);
  const technicalAnalysis = selectTechnicalAnalysis(result);
  const copyableAiAssessmentText = selectCopyableAiAssessmentText(result);

  return (
    <Modal
      opened
      onClose={onClose}
      size="80vw"
      centered
      radius="lg"
      styles={{
        content: {
          maxWidth: "90rem",
          height: "88vh",
        },
        body: {
          height: "100%",
        },
      }}
      title={
        <Group gap="xs">
          <IconSparkles size={18} />
          <Text fw={600}>BREEAM-sjekk for sak {issueId}</Text>
        </Group>
      }
    >
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Text fw={600}>{issueTitle}</Text>
          </Stack>

          <Button
            variant="light"
            leftSection={<IconRefresh size={16} />}
            loading={isRunning}
            disabled={!canStartCheck}
            onClick={rerunCheck}
          >
            Kjør på nytt
          </Button>
        </Group>

        {isCheckingPrerequisites && (
          <Alert color="blue" variant="light" icon={<IconSparkles size={18} />} title="Sjekker underlag">
            <Text size="sm">Kontrollerer at saken har PDF-filer og veiledningstekst før analysen starter.</Text>
          </Alert>
        )}

        {isCheckingPrerequisites === false && missingPrerequisites.length > 0 && (
          <Alert
            color="yellow"
            variant="light"
            icon={<IconAlertCircle size={18} />}
            title="BREEAM-sjekken kan ikke kjøres"
          >
            <Stack gap={4}>
              {missingPrerequisites.map((message) => (
                <Text key={message} size="sm">
                  {message}
                </Text>
              ))}
            </Stack>
          </Alert>
        )}

        {isRunning && (
          <Card withBorder radius="md" padding="lg">
            <Stack gap="md" align="center">
              <Loader size="xl" color="blue" />
              <Alert
                w="100%"
                color="blue"
                variant="light"
                icon={<IconSparkles size={18} className="breeam-loading-icon" />}
                title="Analyserer dokumentasjon"
              >
                <Stack gap={6}>
                  <Text size="sm">Modellen leser underlag, vurderer kriteriet og bygger opp et svar med evidens.</Text>
                  <Text size="sm" c="dimmed" className="breeam-loading-dots">
                    Dette kan ta noen sekunder
                  </Text>
                </Stack>
              </Alert>

              <Group gap="xs">
                <Badge variant="light" color="blue" className="breeam-loading-badge">
                  Dokumenter
                </Badge>
                <Badge
                  variant="light"
                  color="cyan"
                  className="breeam-loading-badge"
                  style={{ animationDelay: "120ms" }}
                >
                  Analyse
                </Badge>
                <Badge
                  variant="light"
                  color="teal"
                  className="breeam-loading-badge"
                  style={{ animationDelay: "240ms" }}
                >
                  Evidens
                </Badge>
              </Group>

              <style>
                {`
                  .breeam-loading-icon {
                    animation: breeamPulse 1.4s ease-in-out infinite;
                  }

                  .breeam-loading-dots::after {
                    content: "...";
                    display: inline-block;
                    width: 1.2em;
                    overflow: hidden;
                    vertical-align: bottom;
                    animation: breeamDots 1.2s steps(4, end) infinite;
                  }

                  .breeam-loading-badge {
                    animation: breeamFloat 1.8s ease-in-out infinite;
                  }

                  @keyframes breeamPulse {
                    0%, 100% { transform: scale(1); opacity: 0.75; }
                    50% { transform: scale(1.08); opacity: 1; }
                  }

                  @keyframes breeamDots {
                    0% { width: 0; }
                    100% { width: 1.2em; }
                  }

                  @keyframes breeamFloat {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-4px); }
                  }
                `}
              </style>
            </Stack>
          </Card>
        )}

        {runError != null && (
          <Alert
            color="red"
            variant="light"
            icon={<IconAlertCircle size={18} />}
            title="Kunne ikke kjøre BREEAM-sjekken"
          >
            <Text size="sm">{getErrorMessage(runError)}</Text>
          </Alert>
        )}

        {result != null && (
          <ScrollArea.Autosize mah="74vh" offsetScrollbars>
            <Stack gap="md">
              <SectionCard title="Oppsummering">
                <Group justify="space-between" align="center">
                  <Text>{parsedAiAssessment.summary || result.summary}</Text>
                  <Badge color={verdictColor} variant="light" size="lg">
                    {effectiveVerdict}
                  </Badge>
                </Group>
              </SectionCard>

              <SectionCard title="Indata">
                <Stack gap="sm">
                  <Card withBorder radius="md" padding="sm">
                    <Stack gap={6}>
                      <Text fw={600} size="sm">
                        {inputAttributeLabel}
                      </Text>
                      <Text size="xs" c="dimmed">
                        Verdien fra other attribute med id `I`
                      </Text>
                      <Divider />
                      <Text size="sm">
                        {result.inputData.otherAttributeIText?.trim() || "Ingen verdi satt på attributt I."}
                      </Text>
                    </Stack>
                  </Card>

                  <Card withBorder radius="md" padding="sm">
                    <Stack gap={6}>
                      <Group justify="space-between" align="center">
                        <Text fw={600} size="sm">
                          PDF-underlag
                        </Text>
                        <Badge variant="light" color="blue">
                          {result.inputData.pdfFileCount} PDF
                        </Badge>
                      </Group>
                      <Divider />
                      {result.inputData.pdfFiles.length > 0 ? (
                        <Stack gap="xs">
                          {result.inputData.pdfFiles.map((file, index) => (
                            <Text key={`${file.blobName}-${index}`} size="sm">
                              {index + 1}. {file.fileName} ({file.extractedTextLength} tegn)
                            </Text>
                          ))}
                        </Stack>
                      ) : (
                        <Text size="sm">Ingen PDF-filer funnet for denne saken.</Text>
                      )}
                    </Stack>
                  </Card>
                </Stack>
              </SectionCard>

              <SectionCard title="Analyse">
                <Stack gap="md">
                  <Group justify="space-between" align="center">
                    <Badge color={verdictColor} variant="light" size="lg">
                      {effectiveVerdict}
                    </Badge>

                    <CopyButton value={copyableAiAssessmentText}>
                      {({ copied, copy }) => (
                        <Button
                          variant={copied ? "filled" : "light"}
                          color={copied ? "teal" : "blue"}
                          size="sm"
                          leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                          onClick={copy}
                        >
                          {copied ? "Analyse kopiert" : "Kopier analyse til utklippstavle"}
                        </Button>
                      )}
                    </CopyButton>
                  </Group>

                  <Card withBorder radius="md" padding="sm">
                    <Stack gap={6}>
                      <Text fw={600} size="sm">
                        Kort oppsummering
                      </Text>
                      <Divider />
                      <Text size="sm">{parsedAiAssessment.summary || "Ingen oppsummering funnet i AI-svaret."}</Text>
                    </Stack>
                  </Card>

                  <Card withBorder radius="md" padding="sm">
                    <Stack gap={6}>
                      <Text fw={600} size="sm">
                        Støttende punkter
                      </Text>
                      <Divider />
                      {parsedAiAssessment.supportingPoints.length > 0 ? (
                        <Stack gap="xs">
                          {parsedAiAssessment.supportingPoints.map((point, index) => (
                            <Text key={`${index}-${point.slice(0, 20)}`} size="sm">
                              • {point}
                            </Text>
                          ))}
                        </Stack>
                      ) : (
                        <Text size="sm" c="dimmed">
                          Ingen støttende punkter funnet i AI-svaret.
                        </Text>
                      )}
                    </Stack>
                  </Card>

                  <Card withBorder radius="md" padding="sm">
                    <Stack gap={6}>
                      <Text fw={600} size="sm">
                        Gap / usikkerheter
                      </Text>
                      <Divider />
                      {parsedAiAssessment.gaps.length > 0 ? (
                        <Stack gap="xs">
                          {parsedAiAssessment.gaps.map((gap, index) => (
                            <Text key={`${index}-${gap.slice(0, 20)}`} size="sm">
                              • {gap}
                            </Text>
                          ))}
                        </Stack>
                      ) : (
                        <Text size="sm" c="dimmed">
                          Ingen gap funnet i AI-svaret.
                        </Text>
                      )}
                    </Stack>
                  </Card>
                </Stack>
              </SectionCard>

              {technicalAnalysis.length > 0 && (
                <Stack gap="xs">
                  <Group justify="flex-end">
                    <Button
                      variant="subtle"
                      color="gray"
                      size="xs"
                      onClick={() => setShowTechnicalDebug((current) => !current)}
                    >
                      {showTechnicalDebug ? "Skjul teknisk debug" : "Vis teknisk debug"}
                    </Button>
                  </Group>

                  {showTechnicalDebug && (
                    <SectionCard title="Teknisk debug">
                      <Stack gap="xs">
                        {technicalAnalysis.map((entry, index) => (
                          <Text key={`${index}-${entry.slice(0, 20)}`} size="sm">
                            {index + 1}. {entry}
                          </Text>
                        ))}
                      </Stack>
                    </SectionCard>
                  )}
                </Stack>
              )}
            </Stack>
          </ScrollArea.Autosize>
        )}
      </Stack>
    </Modal>
  );
};

export default IssueBreeamCriterionCheckModal;
